import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Avatar,
  Rating,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Map as MapIcon, 
  FilterAlt, 
  MyLocation, 
  DarkMode,
  KeyboardArrowDown,
  CheckCircle,
  Speed,
  VerifiedUser,
  Support,
  Business,
  ArrowForward
} from '@mui/icons-material';
import { getTranslation } from '../../i18n';

const LandingPage = () => {
  const { theme, language } = useSelector((state) => state.ui);
  const { lang } = useParams();
  const navigate = useNavigate();
  const translations = getTranslation(lang || language);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isSmall = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Redirect to language-specific URL if needed
  useEffect(() => {
    if (!lang && language) {
      navigate(`/${language}`, { replace: true });
    }
  }, [lang, language, navigate]);

  // Feature cards data
  const features = [
    {
      icon: <Speed fontSize="large" />,
      title: translations.landing.features.map,
      description: translations.landing.features.mapDescription,
      color: '#3F51B5' // Indigo
    },
    {
      icon: <FilterAlt fontSize="large" />,
      title: translations.landing.features.filter,
      description: translations.landing.features.filterDescription,
      color: '#FF9800' // Orange
    },
    {
      icon: <MyLocation fontSize="large" />,
      title: translations.landing.features.location,
      description: translations.landing.features.locationDescription,
      color: '#4CAF50' // Green
    },
    {
      icon: <Business fontSize="large" />,
      title: translations.landing.features.theme,
      description: translations.landing.features.themeDescription,
      color: '#9C27B0' // Purple
    }
  ];

  // Benefits data
  const benefits = [
    {
      icon: <img src="/images/landing/reliability.svg" alt="Reliability" width="60" height="60" />,
      title: translations.landing.benefits.reliability,
      description: translations.landing.benefits.reliabilityDescription,
      color: '#1273D1'
    },
    {
      icon: <img src="/images/landing/accuracy.svg" alt="Accuracy" width="60" height="60" />,
      title: translations.landing.benefits.accuracy,
      description: translations.landing.benefits.accuracyDescription,
      color: '#4CAF50'
    },
    {
      icon: <img src="/images/landing/scalability.svg" alt="Scalability" width="60" height="60" />,
      title: translations.landing.benefits.scalability,
      description: translations.landing.benefits.scalabilityDescription,
      color: '#FF9800'
    },
    {
      icon: <img src="/images/landing/support.svg" alt="Support" width="60" height="60" />,
      title: translations.landing.benefits.support,
      description: translations.landing.benefits.supportDescription,
      color: '#9C27B0'
    }
  ];



  return (
    <Box sx={{ overflow: 'visible' }}>
      {/* Hero Section */}
      <Box 
        sx={{
          background: 'linear-gradient(135deg, #1273D1 0%, #0E58A0 100%)',
          color: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background lightning effect */}
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'url(/images/landing/hero_image.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h2" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 800, 
                      mb: 2,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      textShadow: '0 2px 10px rgba(0,0,0,0.3)'
                    }}
                  >
                    {translations.landing.thunderBG.title}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 4, 
                      fontWeight: 400,
                      opacity: 0.9,
                      maxWidth: 600,
                      textShadow: '0 1px 5px rgba(0,0,0,0.2)'
                    }}
                  >
                    {translations.landing.thunderBG.subtitle}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 4, 
                      maxWidth: 600,
                      opacity: 0.8
                    }}
                  >
                    {translations.landing.thunderBG.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button 
                      variant="contained" 
                      size="large"
                      sx={{
                        backgroundColor: 'white',
                        color: '#1273D1',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                        }
                      }}
                      endIcon={<ArrowForward />}
                      onClick={() => {
                        const mobileAppSection = document.getElementById('mobile-app-section');
                        if (mobileAppSection) {
                          mobileAppSection.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      {translations.landing.ctaButton}
                    </Button>
                    <Button 
                      variant="outlined" 
                      component={Link} 
                      to="/map" 
                      size="large"
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        borderWidth: 2,
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          borderWidth: 2,
                        }
                      }}
                    >
                      {translations.landing.thunderBG.viewDemo}
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
              <Slide direction="left" in={true} timeout={1000}>
                <Box 
                  component="img" 
                  src="/images/landing/hero_image.svg" 
                  alt="SkyRadar Lightning Detection"
                  sx={{
                    width: '100%',
                    maxWidth: 500,
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    transform: 'perspective(1000px) rotateY(-10deg)',
                  }}
                />
              </Slide>
            </Grid>
          </Grid>

          {/* Scroll indicator */}
          <Box 
            sx={{ 
              position: 'absolute', 
              bottom: -40, 
              left: '50%', 
              transform: 'translateX(-50%)',
              textAlign: 'center',
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0) translateX(-50%)',
                },
                '40%': {
                  transform: 'translateY(-20px) translateX(-50%)',
                },
                '60%': {
                  transform: 'translateY(-10px) translateX(-50%)',
                }
              }
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: 40, opacity: 0.7 }} />
          </Box>
        </Container>
      </Box>

      {/* Partners Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              {translations.landing.partners.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                mb: 4
              }}
            >
              {translations.landing.partners.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center" alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 2
                }}
              >
                <Box 
                  component="img" 
                  src="/images/partners/icdsoft_logo.svg" 
                  alt="ICDSoft Logo" 
                  sx={{ 
                    maxWidth: '100%', 
                    height: 'auto', 
                    maxHeight: 80,
                    mb: 2
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translations.landing.partners.icdsoft}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {translations.landing.partners.icdsoftDescription}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: 2
                }}
              >
                <Box 
                  component="img" 
                  src="/images/partners/firebrigade1_logo.svg" 
                  alt="FireBrigade1 Logo" 
                  sx={{ 
                    maxWidth: '100%', 
                    height: 'auto', 
                    maxHeight: 80,
                    mb: 2
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translations.landing.partners.firebrigade}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {translations.landing.partners.firebrigadeDescription}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Data Processing Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 700,
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                {translations.landing.dataProcessing.title}
              </Typography>
              <Typography variant="body1" paragraph>
                {translations.landing.dataProcessing.description1}
              </Typography>
              <Typography variant="body1" paragraph>
                {translations.landing.dataProcessing.description2}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: '#4CAF50', mr: 2 }} />
                <Typography variant="body1" fontWeight={500}>
                  {translations.landing.dataProcessing.feature1}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CheckCircle sx={{ color: '#4CAF50', mr: 2 }} />
                <Typography variant="body1" fontWeight={500}>
                  {translations.landing.dataProcessing.feature2}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ color: '#4CAF50', mr: 2 }} />
                <Typography variant="body1" fontWeight={500}>
                  {translations.landing.dataProcessing.feature3}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box 
                component="img" 
                src="/images/landing/data_processing.svg" 
                alt="Data Processing" 
                sx={{ 
                  width: '100%', 
                  height: 'auto',
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              {translations.landing.features.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.features.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                  <Card 
                    elevation={3} 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    <Box 
                      sx={{ 
                        p: 3, 
                        backgroundColor: feature.color,
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              {translations.landing.benefits.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.benefits.description}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Fade in={true} timeout={1000} style={{ transitionDelay: `${index * 200}ms` }}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      height: '100%', 
                      p: 4,
                      borderRadius: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ mb: 3 }}>
                      {benefit.icon}
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: benefit.color }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {benefit.description}
                    </Typography>
                  </Paper>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>



      {/* Mobile App Section */}
      <Box id="mobile-app-section" sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              {translations.landing.mobileApp.title}
            </Typography>
            <Typography 
              variant="h6" 
              color="textSecondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                mb: 2
              }}
            >
              {translations.landing.mobileApp.subtitle}
            </Typography>
          </Box>

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    {translations.landing.mobileApp.heading}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {translations.landing.mobileApp.description}
                  </Typography>
                  <Typography variant="body1" paragraph sx={{ fontWeight: 500, color: '#1273D1' }}>
                    {translations.landing.mobileApp.highlight}
                  </Typography>

                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </ListItemIcon>
                      <ListItemText primary="Real-time lightning strike notifications" secondary="Get alerts when lightning is detected near your location" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </ListItemIcon>
                      <ListItemText primary="Interactive lightning map with filters" secondary="Visualize lightning activity with customizable filters" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </ListItemIcon>
                      <ListItemText primary="Customizable alert radius and settings" secondary="Set your preferred notification distance and alert types" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </ListItemIcon>
                      <ListItemText primary="Weather forecasts with lightning risk assessment" secondary="Plan your activities with lightning risk forecasts" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle sx={{ color: '#4CAF50' }} />
                      </ListItemIcon>
                      <ListItemText primary="Works offline with cached data" secondary="Access recent data even without internet connection" />
                    </ListItem>
                  </List>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 4 }}>
                    <Button 
                      variant="contained" 
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        backgroundColor: '#1273D1'
                      }}
                      disabled
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="button">{translations.landing.mobileApp.downloadIos}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{translations.landing.mobileApp.comingSoon}</Typography>
                      </Box>
                    </Button>
                    <Button 
                      variant="contained" 
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        backgroundColor: '#4CAF50'
                      }}
                      disabled
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="button">{translations.landing.mobileApp.downloadAndroid}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{translations.landing.mobileApp.comingSoon}</Typography>
                      </Box>
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', height: 500, display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src="/images/mobile_app/app_screenshot1.svg"
                  alt="SkyRadar Mobile App - Map View"
                  sx={{
                    position: 'absolute',
                    height: 450,
                    top: 0,
                    left: { xs: 'calc(50% - 150px)', md: 0 },
                    zIndex: 3,
                    borderRadius: 4,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    transform: 'rotate(-5deg)',
                  }}
                />
                <Box
                  component="img"
                  src="/images/mobile_app/app_screenshot2.svg"
                  alt="SkyRadar Mobile App - Alerts View"
                  sx={{
                    position: 'absolute',
                    height: 450,
                    top: 25,
                    left: { xs: 'calc(50% - 75px)', md: 75 },
                    zIndex: 2,
                    borderRadius: 4,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                  }}
                />
                <Box
                  component="img"
                  src="/images/mobile_app/app_screenshot3.svg"
                  alt="SkyRadar Mobile App - Forecast View"
                  sx={{
                    position: 'absolute',
                    height: 450,
                    top: 50,
                    left: { xs: 'calc(50% - 0px)', md: 150 },
                    zIndex: 1,
                    borderRadius: 4,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                    transform: 'rotate(5deg)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Sensor Infrastructure Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Slide direction="right" in={true} timeout={1000}>
                <Paper 
                  elevation={6} 
                  sx={{ 
                    borderRadius: 4, 
                    overflow: 'hidden',
                    height: { xs: 300, md: 400 },
                    backgroundImage: 'url(/images/landing/sensor_network.svg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              </Slide>
            </Grid>
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h3" 
                    component="h2" 
                    sx={{ 
                      mb: 4, 
                      fontWeight: 700,
                      fontSize: { xs: '2rem', md: '2.5rem' }
                    }}
                  >
                    {translations.landing.sensorInfrastructure.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {translations.landing.sensorInfrastructure.description}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontStyle: 'italic',
                      borderLeft: '4px solid #1273D1',
                      pl: 2,
                      py: 1
                    }}
                  >
                    {translations.landing.sensorInfrastructure.quote}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button 
                      variant="contained" 
                      component={Link} 
                      to="/sensor-infrastructure" 
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1
                      }}
                      endIcon={<ArrowForward />}
                    >
                      {translations.landing.sensorInfrastructure.learnMore}
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h3" 
                    component="h2" 
                    sx={{ 
                      mb: 4, 
                      fontWeight: 700,
                      fontSize: { xs: '2rem', md: '2.5rem' }
                    }}
                  >
                    {translations.landing.about.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {translations.landing.about.description}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph 
                    sx={{ 
                      fontStyle: 'italic',
                      borderLeft: '4px solid #1273D1',
                      pl: 2,
                      py: 1
                    }}
                  >
                    {translations.landing.about.mission}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                    <Button 
                      variant="contained" 
                      component={Link} 
                      to="/contact" 
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1
                      }}
                      endIcon={<ArrowForward />}
                    >
                      {translations.landing.getStarted}
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Slide direction="right" in={true} timeout={1000}>
                <Paper 
                  elevation={6} 
                  sx={{ 
                    borderRadius: 4, 
                    overflow: 'hidden',
                    height: { xs: 300, md: 400 },
                    backgroundImage: 'url(/images/landing/hero_image.svg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 }, 
          backgroundColor: '#1273D1',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h4" 
            component="h2" 
            sx={{ 
              mb: 3, 
              fontWeight: 700,
              fontSize: { xs: '1.75rem', md: '2.25rem' }
            }}
          >
            {translations.landing.cta.title}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4, 
              opacity: 0.9,
              maxWidth: 700,
              mx: 'auto'
            }}
          >
            {translations.landing.cta.description}
          </Typography>
          <Button 
            variant="contained" 
            component={Link} 
            to="/contact" 
            size="large"
            sx={{
              backgroundColor: 'white',
              color: '#1273D1',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
              }
            }}
            endIcon={<ArrowForward />}
          >
            {translations.landing.cta.button}
          </Button>
        </Container>
      </Box>

    </Box>
  );
};

export default LandingPage;
